import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AUTOFILLED_BY_PERSON_ID } from '@/configuration/client';
import getListOrTableField from '@/helpers/getListOrTableField';
import { trackQuestionnaireRenderLog } from '@/helpers/questionnaires/renderLogging';
import type * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

import areValuesEqual from '../../form/helpers/areValuesEqual';
import createEmptyRow from '../../form/helpers/createEmptyRow';
import getLinkedListKeys from '../../form/helpers/getLinkedListKeys';
import isEmptyValue from '../../form/helpers/isEmptyValue';
import parseFieldKey from '../../form/helpers/parseFieldKey';
import { useEditingState } from '../EditingStateProvider';
import useQuestionnaireContext from '../useQuestionnaireContext';

import QuestionnaireFormContext, {
  type AccumulatedModalChanges,
  type QuestionnaireFormContextType,
  defaultAutoSave,
} from './QuestionnaireFormContext';

type QuestionnaireFormProviderProps = {
  validationRequired?: boolean;
  formMethods: QuestionnaireFormContextType['formMethods'];
  id: string;
};

const isRowFieldKey = (rowFieldKey: string, fullFieldKey: string): boolean =>
  fullFieldKey === rowFieldKey ||
  fullFieldKey.startsWith(`${rowFieldKey}[`) ||
  fullFieldKey.startsWith(`${rowFieldKey}.`);

const QuestionnaireFormProvider: React.FC<PropsWithChildren<QuestionnaireFormProviderProps>> = ({
  children,
  validationRequired = false,
  formMethods,
  id,
}) => {
  trackQuestionnaireRenderLog('Render QuestionnaireFormProvider', id);
  const { fieldsById, fieldsByKey, questionnaireResponses, formData, updateFieldResponseMetadata, logger } =
    useQuestionnaireContext();
  const { isFieldBeingEdited } = useEditingState();
  const [metaDataIsInitialized, setMetaDataIsInitialized] = useState(false);
  const fieldsByKeyRef = useRef(fieldsByKey);
  fieldsByKeyRef.current = fieldsByKey;
  const fieldsByIdRef = useRef(fieldsById);
  fieldsByIdRef.current = fieldsById;

  const isModalContext = id.startsWith('Modal');
  const modalLevel = useMemo(() => {
    if (!isModalContext) return 0;
    if (id === 'Modal') return 1;
    const match = id.match(/Modal-Level-(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }, [id, isModalContext]);

  // Store accumulated changes from nested modals only
  const accumulatedModalChanges = useRef<AccumulatedModalChanges>({
    aiRequestIds: new Map(),
    autofillStatuses: new Map(),
    explanationHtmls: new Map(),
    responseSourceIds: new Map(),
    responseSources: new Map(),
    values: new Map(),
  });

  const responseSourceIndexesRef = useRef<Record<string, QuestionnaireApiModels.QuestionnaireResponse['sourceIndex']>>(
    {},
  );
  const getResponseSourceIndex = useCallback<QuestionnaireFormContextType['getResponseSourceIndex']>(
    (fieldKey) => responseSourceIndexesRef.current[fieldKey],
    [],
  );
  const setResponseSourceIndex = useCallback<QuestionnaireFormContextType['setResponseSourceIndex']>(
    (fieldKey, sourceIndex) => {
      responseSourceIndexesRef.current[fieldKey] = sourceIndex;
    },
    [],
  );

  const responseSourceIdsRef = useRef<Record<string, QuestionnaireApiModels.QuestionnaireResponse['sourceId']>>({});
  const getResponseSourceId = useCallback<QuestionnaireFormContextType['getResponseSourceId']>(
    (fieldKey) => responseSourceIdsRef.current[fieldKey],
    [],
  );
  const setResponseSourceId = useCallback<QuestionnaireFormContextType['setResponseSourceId']>(
    (fieldKey, sourceId) => {
      responseSourceIdsRef.current[fieldKey] = sourceId;
      if (isModalContext) {
        accumulatedModalChanges.current.responseSourceIds.set(fieldKey, sourceId);
      }
    },
    [isModalContext],
  );

  const responseCorrelationIdsRef = useRef<Record<string, string | null>>({});
  const getResponseCorrelationId = useCallback<QuestionnaireFormContextType['getResponseCorrelationId']>(
    (fieldKey) => responseCorrelationIdsRef.current[fieldKey],
    [],
  );
  const setResponseCorrelationId = useCallback<QuestionnaireFormContextType['setResponseCorrelationId']>(
    (fieldKey, correlationId) => {
      responseCorrelationIdsRef.current[fieldKey] = correlationId;
    },
    [],
  );

  const sourceIdsToListRowIdMapRef = useRef<Record<string, string>>({});
  const getListRowIdFromSourceId = useCallback<QuestionnaireFormContextType['getResponseSourceId']>(
    (sourceId) => sourceIdsToListRowIdMapRef.current[sourceId] ?? null,
    [],
  );

  const responseSourcesRef = useRef<Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>>({});
  const getResponseSource = useCallback<QuestionnaireFormContextType['getResponseSource']>(
    (fieldKey) => responseSourcesRef.current[fieldKey],
    [],
  );
  const setResponseSource = useCallback<QuestionnaireFormContextType['setResponseSource']>(
    (fieldKey, source) => {
      responseSourcesRef.current[fieldKey] = source;
      if (isModalContext) {
        accumulatedModalChanges.current.responseSources.set(fieldKey, source);
      }
    },
    [isModalContext],
  );

  /**
   * Flag to indicate if the value can be autosaved to the database.
   * This is used to prevent autosaving when the user is editing a field in a modal
   * The autosave will only happen when the user clicks on the save button in the modal.
   * This prevents autosave from saving empty list items to DB
   */
  const canSaveValueToDB = useRef<boolean>(true);
  const getCanSaveValueToDB = useCallback<QuestionnaireFormContextType['getCanSaveValueToDB']>(
    () => canSaveValueToDB.current,
    [],
  );
  const setCanSaveValueToDB = useCallback<QuestionnaireFormContextType['setCanSaveValueToDB']>((value: boolean) => {
    canSaveValueToDB.current = value;
  }, []);

  const getBulkResponseSources = useCallback<QuestionnaireFormContextType['getBulkResponseSources']>((args) => {
    if (!args) {
      return responseSourcesRef.current;
    }
    const subFieldSources: ReturnType<QuestionnaireFormContextType['getBulkResponseSources']> = {};
    Object.entries(responseSourcesRef.current).forEach(([fullFieldKey, source]) => {
      if (isRowFieldKey(args.rowFieldKey, fullFieldKey)) {
        subFieldSources[fullFieldKey] = source;
      }
    });
    return subFieldSources;
  }, []);
  const mergeResponseSources = useCallback<QuestionnaireFormContextType['mergeResponseSources']>((sources) => {
    Object.entries(sources).forEach(([key, source]) => {
      responseSourcesRef.current[key] = source;
    });
  }, []);

  const autofillStatusRef = useRef<Record<string, boolean>>({});
  const getAutofillStatus = useCallback<QuestionnaireFormContextType['getAutofillStatus']>(
    (fieldKey) => autofillStatusRef.current[fieldKey] ?? false,
    [],
  );
  const setAutofillStatus = useCallback<QuestionnaireFormContextType['setAutofillStatus']>(
    (fieldKey, status) => {
      autofillStatusRef.current[fieldKey] = status;
      if (isModalContext) {
        accumulatedModalChanges.current.autofillStatuses.set(fieldKey, status);
      }
    },
    [isModalContext],
  );
  const getBulkAutofillStatuses = useCallback<QuestionnaireFormContextType['getBulkAutofillStatuses']>((args) => {
    if (!args) {
      return autofillStatusRef.current;
    }
    const subFieldStatuses: ReturnType<QuestionnaireFormContextType['getBulkAutofillStatuses']> = {};
    Object.entries(autofillStatusRef.current).forEach(([fullFieldKey, status]) => {
      if (isRowFieldKey(args?.rowFieldKey, fullFieldKey)) {
        subFieldStatuses[fullFieldKey] = status;
      }
    });
    return subFieldStatuses;
  }, []);
  const mergeAutofillStatuses = useCallback<QuestionnaireFormContextType['mergeAutofillStatuses']>((statuses) => {
    Object.entries(statuses).forEach(([key, status]) => {
      autofillStatusRef.current[key] = status;
    });
  }, []);

  const aiRequestIdRef = useRef<Record<string, string | null>>({});
  const getAiRequestId = useCallback<QuestionnaireFormContextType['getAiRequestId']>(
    (fieldKey): string | null => aiRequestIdRef.current[fieldKey] ?? null,
    [],
  );
  const setAiRequestId = useCallback<QuestionnaireFormContextType['setAiRequestId']>(
    (fieldKey, aiRequestIdLocal) => {
      aiRequestIdRef.current[fieldKey] = aiRequestIdLocal;
      if (isModalContext) {
        accumulatedModalChanges.current.aiRequestIds.set(fieldKey, aiRequestIdLocal);
      }
    },
    [isModalContext],
  );

  const getBulkAiRequestIds = useCallback<QuestionnaireFormContextType['getBulkAiRequestIds']>((args) => {
    if (!args) {
      return aiRequestIdRef.current;
    }
    const subFieldAiRequestIds: ReturnType<QuestionnaireFormContextType['getBulkAiRequestIds']> = {};
    Object.entries(aiRequestIdRef.current).forEach(([fullFieldKey, aiRequestId]) => {
      if (isRowFieldKey(args.rowFieldKey, fullFieldKey)) {
        subFieldAiRequestIds[fullFieldKey] = aiRequestId;
      }
    });
    return subFieldAiRequestIds;
  }, []);
  const mergeAiRequestIds = useCallback<QuestionnaireFormContextType['mergeAiRequestIds']>((aiRequestIds) => {
    Object.entries(aiRequestIds).forEach(([key, aiRequestId]) => {
      aiRequestIdRef.current[key] = aiRequestId;
    });
  }, []);

  const explanationHtmlRef = useRef<Record<string, string | null>>({});
  const getExplanationHtml = useCallback<QuestionnaireFormContextType['getExplanationHtml']>(
    (fieldKey) => explanationHtmlRef.current[fieldKey] ?? null,
    [],
  );
  const setExplanationHtml = useCallback<QuestionnaireFormContextType['setExplanationHtml']>(
    (fieldKey, explanationHtml) => {
      explanationHtmlRef.current[fieldKey] = explanationHtml;
      if (isModalContext) {
        accumulatedModalChanges.current.explanationHtmls.set(fieldKey, explanationHtml);
      }
    },
    [isModalContext],
  );

  const mergeMetadataFromContext = useCallback<QuestionnaireFormContextType['mergeMetadataFromContext']>(
    (fromContext) => {
      mergeResponseSources(fromContext.getBulkResponseSources());
      mergeAiRequestIds(fromContext.getBulkAiRequestIds());
      mergeAutofillStatuses(fromContext.getBulkAutofillStatuses());
    },
    [mergeResponseSources, mergeAiRequestIds, mergeAutofillStatuses],
  );

  const removeRowMetaData = useCallback<QuestionnaireFormContextType['removeRowMetaData']>(
    (listFieldKey: string, removedIndex: number) => {
      // Get all field templates for the list to know what subfields to update
      const listField = fieldsByKeyRef.current[listFieldKey];
      if (!listField?.listItemTemplates) return;

      const subFieldKeys = listField.listItemTemplates.map((template) => template.key);

      // For each metadata store, we need to:
      // 1. Collect all metadata for indices > removedIndex
      // 2. Remove the old entries
      // 3. Re-add with adjusted indices (shifted down by 1)

      // Helper function to adjust metadata for a specific store
      const adjustMetadataStore = <T extends Record<string, unknown>>(metadataRef: { current: T }): void => {
        const metadataToAdjust: Array<{ oldKey: string; newKey: string; value: T[keyof T] }> = [];
        const keysToRemove: string[] = [];

        // Pre-compile regex pattern and build subfield lookup for efficiency
        const listFieldPattern = new RegExp(
          `^${listFieldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\[(\\d+)\\]\\.(.+)$`,
        );
        const subFieldSet = new Set(subFieldKeys);

        // Single pass through metadata entries - O(n) instead of O(n*m)
        Object.entries(metadataRef.current).forEach(([key, value]) => {
          const match = key.match(listFieldPattern);
          if (!match) return;

          const index = parseInt(match[1], 10);
          const subFieldKey = match[2];

          // Only process if it's a valid subfield
          if (!subFieldSet.has(subFieldKey)) return;

          // Remove metadata for the deleted item
          if (index === removedIndex) {
            keysToRemove.push(key);
          } else if (index > removedIndex) {
            // Adjust metadata for items after the deleted one
            const newIndex = index - 1;
            const newKey = `${listFieldKey}[${newIndex}].${subFieldKey}`;
            metadataToAdjust.push({ newKey, oldKey: key, value: value as T[keyof T] });
            keysToRemove.push(key);
          }
        });

        // Remove old entries
        keysToRemove.forEach((key) => {
          delete metadataRef.current[key];
        });

        // Add adjusted entries
        metadataToAdjust.forEach(({ newKey, value }) => {
          // Keys are dynamically generated and managed; indexing with string is intentional.
          metadataRef.current[newKey as keyof T] = value;
        });
      };

      // Adjust all metadata stores
      adjustMetadataStore(responseSourcesRef);
      adjustMetadataStore(responseSourceIdsRef);
      adjustMetadataStore(responseCorrelationIdsRef);
      adjustMetadataStore(responseSourceIndexesRef);
      adjustMetadataStore(aiRequestIdRef);
      adjustMetadataStore(autofillStatusRef);
      adjustMetadataStore(explanationHtmlRef);
    },
    [],
  );

  const clearListMetaData = useCallback<QuestionnaireFormContextType['clearListMetaData']>((listFieldKey) => {
    // Remove all metadata entries associated with the given listFieldKey from all metadata stores
    const removeKeysForList = (metadataRef: React.MutableRefObject<Record<string, unknown>>): void => {
      const keysToRemove: string[] = [];
      Object.keys(metadataRef.current).forEach((key) => {
        // Match keys that start with `${listFieldKey}[` (for list/table items)
        if (key.startsWith(`${listFieldKey}[`)) {
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach((key) => {
        delete metadataRef.current[key];
      });
    };

    removeKeysForList(responseSourcesRef);
    removeKeysForList(responseSourceIdsRef);
    removeKeysForList(responseCorrelationIdsRef);
    removeKeysForList(responseSourceIndexesRef);
    removeKeysForList(aiRequestIdRef);
    removeKeysForList(autofillStatusRef);
    removeKeysForList(explanationHtmlRef);
  }, []);

  const hasFetchedAllSections = useMemo(
    () => formData.sections.every((section) => !section.isLoading),
    [formData.sections],
  );

  useEffect(() => {
    if (isModalContext) {
      // When opening in a modal, count on the modal to populate the values and metadata
      setMetaDataIsInitialized(true);
      return;
    }
    setMetaDataIsInitialized(false);
    const newFieldSources: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']> = {};
    const newFieldSourceIds: Record<string, QuestionnaireApiModels.QuestionnaireResponse['sourceId']> = {};
    const newFieldCorrelationIds: Record<string, string | null> = {};
    const newFieldSourceIndexes: Record<string, QuestionnaireApiModels.QuestionnaireResponse['sourceIndex']> = {};
    const newAiRequestIds: Record<string, string | null> = {};
    const newAutofillStatuses: Record<string, boolean> = {};
    const newExplanationHtmls: Record<string, string | null> = {};
    const newSourceIdToListRowIdMap: Record<string, string> = {};

    (questionnaireResponses ?? []).forEach((fieldResponse) => {
      const fieldTemplate = fieldsByIdRef.current[fieldResponse.fieldTemplateId];
      if (!fieldTemplate) {
        return;
      }

      if (fieldTemplate.type === 'list' || fieldTemplate.type === 'table') {
        if (fieldResponse.sourceId) {
          newSourceIdToListRowIdMap[fieldResponse.sourceId] = fieldResponse.id ?? null;
        }
        return;
      }

      const listOrTableParent = getListOrTableField({
        fieldId: fieldResponse.fieldTemplateId,
        fieldsById: fieldsByIdRef.current,
      });

      const keys = listOrTableParent
        ? getLinkedListKeys({ fieldTemplate, fieldsById: fieldsByIdRef.current })
        : [fieldTemplate.key];
      keys.forEach((key) => {
        const cellKey = listOrTableParent ? `${listOrTableParent.key}[${fieldResponse.index}].${key}` : key;
        newFieldSources[cellKey] = fieldResponse.source;
        newAiRequestIds[cellKey] = fieldResponse.aiRequestId ?? null;
        newAutofillStatuses[cellKey] = fieldResponse.hasAutofillRun ?? false;
        newExplanationHtmls[cellKey] = fieldResponse.explanationHtml ?? null;
        newFieldSourceIds[cellKey] = fieldResponse.sourceId ?? null;
        newFieldCorrelationIds[cellKey] = fieldResponse.correlationId ?? null;
        newFieldSourceIndexes[cellKey] = fieldResponse.sourceIndex ?? null;
      });
    });
    responseSourcesRef.current = newFieldSources;
    responseSourceIdsRef.current = newFieldSourceIds;
    responseCorrelationIdsRef.current = newFieldCorrelationIds;
    responseSourceIndexesRef.current = newFieldSourceIndexes;
    aiRequestIdRef.current = newAiRequestIds;
    autofillStatusRef.current = newAutofillStatuses;
    explanationHtmlRef.current = newExplanationHtmls;
    sourceIdsToListRowIdMapRef.current = newSourceIdToListRowIdMap;
    setMetaDataIsInitialized(true);
  }, [questionnaireResponses, hasFetchedAllSections, isModalContext]);

  const setValue = useCallback<QuestionnaireFormContextType['setValue']>(
    ({ key, value, source, sourceId, aiRequestId, hasAutofillRun = false, explanationHtml }): void => {
      // FOCUS-BASED PROTECTION: Prevent external updates to fields that are currently being edited
      // This fixes the issue where autofill, AI agents, etc. would overwrite user input while typing.
      // We allow:
      // - Manual updates (user typing, source='manual')
      // - Updates from setValues calls (which don't have a source)
      // We block:
      // - Autofill updates (source='autofill', 'conditional-fill', etc.)
      // - AI agent updates (source='ai')
      // - Linked field updates (source='linked-field')
      // - Any other external updates with a source
      // Note: Real-time sync is protected separately in HandleRealTimeSync since it bypasses this function
      if (source && source !== 'manual' && isFieldBeingEdited(key)) {
        logger.info('Field is being edited, skipping update', { key, source });
        return;
      }

      const currentValue = formMethods.getValues(key);
      // Because nested objects become dirty if strict equal changes, even if deep stays the same.
      // Check for deep equality before updating the RHF value.
      const currentAutofillStatus = getAutofillStatus(key);
      if (areValuesEqual(currentValue, value) && hasAutofillRun === currentAutofillStatus) {
        return;
      }
      // If the current value is not set, and the new value is "empty" no need to set it.
      if ((currentValue === undefined || currentValue === null) && hasAutofillRun === currentAutofillStatus) {
        const parsedKey = parseFieldKey(key);
        const fieldTemplate = parsedKey.fieldKey && fieldsByKeyRef.current[parsedKey.fieldKey];
        if (fieldTemplate) {
          if (isEmptyValue(value, fieldTemplate)) {
            return;
          }
        }
      }

      if (isModalContext) {
        accumulatedModalChanges.current.values.set(key, value);
      }
      // @todo - it would be nice if we checked the source here and didn't allow setting if "manual" unless new source is also "manual"

      if (source) {
        setResponseSource(key, source);
      }
      if (sourceId) {
        setResponseSourceId(key, sourceId);
      }
      if (aiRequestId !== undefined) {
        setAiRequestId(key, aiRequestId);
      }
      setAutofillStatus(key, hasAutofillRun);
      if (explanationHtml) {
        setExplanationHtml(key, explanationHtml);
      }
      if (source !== 'ai') {
        setExplanationHtml(key, null);
      }

      // Update field response metadata for non-manual sources
      if (source && source !== 'manual') {
        updateFieldResponseMetadata(key, {
          submittedById: AUTOFILLED_BY_PERSON_ID,
          updatedAt: new Date().toISOString(),
        });
      }

      logger.info('Setting value', {
        hasAutofillRun,
        key,
        source,
        value,
      });

      const parsedKey = parseFieldKey(key);

      // Ensure that the row for the table exists, this is only for tables.
      // When adding rows to lists we should make sure that the row exists separately.
      if (parsedKey.listFieldKey && parsedKey.rowIndex !== null) {
        const parentField = fieldsByKeyRef.current[parsedKey.listFieldKey];
        const rowFieldKey = `${parsedKey.listFieldKey}[${parsedKey.rowIndex}]`;
        if (parentField && (parentField.type === 'table' || parentField.type === 'list')) {
          const rowValue = formMethods.getValues(rowFieldKey);
          if (!rowValue) {
            const newRow = createEmptyRow();
            formMethods.setValue(rowFieldKey, newRow, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
            logger.info('Setting row id', { id: newRow._id, key: rowFieldKey });
          } else if (!rowValue._id) {
            const newRow = { ...createEmptyRow(), ...rowValue };
            formMethods.setValue(rowFieldKey, newRow, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
            logger.info('Setting row id', { id: newRow._id, key: rowFieldKey });
          }
        }
      }

      formMethods.setValue(key, value, { shouldDirty: true, shouldTouch: true });
      if (validationRequired && formMethods.getFieldState(key).error) {
        formMethods.clearErrors(key);
      }
    },
    [
      formMethods,
      getAutofillStatus,
      isFieldBeingEdited,
      isModalContext,
      logger,
      setAiRequestId,
      setAutofillStatus,
      setExplanationHtml,
      setResponseSource,
      setResponseSourceId,
      updateFieldResponseMetadata,
      validationRequired,
    ],
  );

  const applyNestedModalChanges = useCallback(
    (changes: AccumulatedModalChanges) => {
      // Store the row's metadata and merge it to the current state
      // Then update the values.
      if (changes.responseSources.size > 0) {
        changes.responseSources.forEach((value, key) => {
          setResponseSource(key, value);
        });
      }
      if (changes.aiRequestIds.size > 0) {
        changes.aiRequestIds.forEach((value, key) => {
          setAiRequestId(key, value);
        });
      }
      if (changes.autofillStatuses.size > 0) {
        changes.autofillStatuses.forEach((value, key) => {
          setAutofillStatus(key, value);
        });
      }
      if (changes.explanationHtmls.size > 0) {
        changes.explanationHtmls.forEach((value, key) => {
          setExplanationHtml(key, value);
        });
      }
      changes.values.forEach((value, key) => {
        setValue({ key, value });
      });
    },
    [setResponseSource, setAiRequestId, setAutofillStatus, setExplanationHtml, setValue],
  );

  // Get all accumulated changes (from nested modals)
  const getAccumulatedModalChanges = useCallback(
    () => ({
      aiRequestIds: accumulatedModalChanges.current.aiRequestIds,
      autofillStatuses: accumulatedModalChanges.current.autofillStatuses,
      explanationHtmls: accumulatedModalChanges.current.explanationHtmls,
      responseSourceIds: accumulatedModalChanges.current.responseSourceIds,
      responseSources: accumulatedModalChanges.current.responseSources,
      values: accumulatedModalChanges.current.values,
    }),
    [],
  );

  const isInitialized = metaDataIsInitialized;
  const values = useMemo(() => {
    trackQuestionnaireRenderLog('QuestionnaireFormContext - valuesMemoized');

    const newContextValues: QuestionnaireFormContextType = {
      applyNestedModalChanges,
      autoSave: defaultAutoSave,
      clearListMetaData,
      validationRequired,
      formContextId: id,
      formMethods,
      getAccumulatedModalChanges,
      getAiRequestId,
      getAutofillStatus,
      getBulkAiRequestIds,
      getBulkAutofillStatuses,
      getBulkResponseSources,
      getCanSaveValueToDB,
      getExplanationHtml,
      getListRowIdFromSourceId,
      getResponseCorrelationId,
      getResponseSource,
      getResponseSourceId,
      getResponseSourceIndex,
      isModalContext,
      mergeAiRequestIds,
      mergeAutofillStatuses,
      mergeMetadataFromContext,
      mergeResponseSources,
      modalLevel,
      removeRowMetaData,
      setAiRequestId,
      setAutofillStatus,
      setCanSaveValueToDB,
      setExplanationHtml,
      setResponseCorrelationId,
      setResponseSource,
      setResponseSourceId,
      setResponseSourceIndex,
      setValue,
    };
    return newContextValues;
  }, [
    id,
    clearListMetaData,
    formMethods,
    getAiRequestId,
    getBulkAiRequestIds,
    getBulkResponseSources,
    getExplanationHtml,
    getListRowIdFromSourceId,
    getResponseCorrelationId,
    getResponseSource,
    getResponseSourceId,
    getResponseSourceIndex,
    setResponseCorrelationId,
    setResponseSourceIndex,
    mergeAiRequestIds,
    mergeResponseSources,
    removeRowMetaData,
    setAiRequestId,
    setExplanationHtml,
    setResponseSource,
    setResponseSourceId,
    setValue,
    getAutofillStatus,
    getBulkAutofillStatuses,
    setAutofillStatus,
    mergeAutofillStatuses,
    mergeMetadataFromContext,
    isModalContext,
    modalLevel,
    getAccumulatedModalChanges,
    applyNestedModalChanges,
    setCanSaveValueToDB,
    getCanSaveValueToDB,
    validationRequired,
  ]);

  return (
    <QuestionnaireFormContext.Provider value={values}>{isInitialized && children}</QuestionnaireFormContext.Provider>
  );
};

export default QuestionnaireFormProvider;
